/**
 * Chef kitchen-list pages — Ingredients (/shopping-list), Recipes
 * (/recipes), Deliverables (/deliverables).
 *
 * These were the three tabs of the old /shopping-list page until the
 * Aug 2026 chef-sidebar split gave each its own sidebar row + page.
 * They still share one implementation (`ChefKitchenPage`) because the
 * views share everything but the list body: the active-kitchen gate,
 * the URL-synced date range, the shipments + aggregation queries, the
 * order-window model, and the print machinery. The shared TanStack
 * query keys also make flipping between the three pages free — the
 * second page renders from cache.
 *
 * Fully on the REAL backend: ingredient rows come from
 * `useShoppingListQuery` (`/api/shopping-list` — recipe-quantity ×
 * shipment-line demand aggregated server-side), and the range's
 * shipments (`useKitchenShipmentsQuery` + `shipmentsToCampuses`)
 * drive the service-day calendar + the print packet's campus pages.
 * The printed Ingredient List section reuses the real (placed-order)
 * rows via `PrintTarget.ingredients`.
 *
 * Look-ahead forecast: while the selected range's order window is
 * still open (`buildChefOrderCalendar`, same model as
 * `/orders/kitchen`), the page requests `includeForecast=true` and the
 * server fills zero-shipment days with a menu-derived estimate. Those
 * rows render visibly distinct (muted `~qty` + a Forecast chip) under
 * a loud "Forecast — not actual orders" banner, and are EXCLUDED from
 * the print packet (`printableShoppingListRows`) — printed documents
 * reflect placed orders only.
 */

import { Alert } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogContent, DialogTitle } from '@ordo/ui/dialog'
import { EmptyState } from '@ordo/ui/empty-state'
import { listenAfterPrintTeardown, usePrintAction } from '@ordo/ui/use-print-action'
import { ArrowLeftRight, ChefHat, Printer, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import { useKitchenShipmentsQuery, useMyKitchensQuery } from '@/api/kitchen-orders'
import {
  printableShoppingListRows,
  type ShoppingListCategory,
  shoppingListRowBrand,
  type ShoppingListRowSource,
  useShoppingListQuery,
} from '@/api/shopping-list'
import { CHIP_CLASS, CHIP_CLASS_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import { DateRangeStepper } from '@/components/chefs/date-range-stepper'
import { DeliverableDetailPanel } from '@/components/chefs/deliverable-detail-panel'
import { type BrandPickerIngredient, IngredientBrandPicker } from '@/components/chefs/ingredient-brand-picker'
import { IngredientSubstitutionDisplay } from '@/components/chefs/ingredient-substitution-display'
import { ListGroupHeader } from '@/components/chefs/list-group-header'
import { MobileActionPill } from '@/components/chefs/mobile-action-pill'
import { NoServiceEmptyState } from '@/components/chefs/no-service-empty-state'
import { PreliminaryPrintConfirm } from '@/components/chefs/print/preliminary-print-confirm'
import { PrintEmptySheet } from '@/components/chefs/print/print-empty-sheet'
import { type PrintDay, PrintRoot, type PrintSections, type PrintTarget } from '@/components/chefs/print/print-root'
import { ShoppingDeliverablesTab } from '@/components/chefs/shopping-deliverables-tab'
import { ShoppingRecipesTab } from '@/components/chefs/shopping-recipes-tab'
import { SubstitutionModal } from '@/components/chefs/substitution-modal'
import { SubstitutionResolutionBanner } from '@/components/chefs/substitution-resolution-banner'
import { SuppliesTable } from '@/components/chefs/supplies-table'
import { TempLogGate } from '@/components/chefs/temp-log-gate'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import {
  RecipeDetailFullscreenDialog,
  RecipeDetailNarrowOverlay,
  RecipeDetailSlot,
  useRecipeDetailSlot,
} from '@/components/recipes/recipe-detail-slot'
import { ActiveKitchenProvider, useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { useChefDateRange } from '@/hooks/chefs/useChefDateRange'
import { useIsNarrow } from '@/hooks/chefs/useIsNarrow'
import { useIsScrolled } from '@/hooks/chefs/useIsScrolled'
import { MeasurementPreferenceProvider } from '@/hooks/chefs/useMeasurementPreference'
import { useOnPassed } from '@/hooks/chefs/useOnPassed'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { buildDeliverablesDayGroups } from '@/lib/chefs/deliverables-by-day'
import { formatShortDay, formatShortDayRange } from '@/lib/chefs/format'
import { formatIngredientQuantity, type MergedIngredient } from '@/lib/chefs/ingredients'
import { buildChefOrderCalendar } from '@/lib/chefs/order-calendar'
import { printAvailability } from '@/lib/chefs/print-availability'
import { nextPrintLockAt, printPreliminary } from '@/lib/chefs/print-lock'
import { buildPrintStructure, printDocumentTitle, recipeKey, resolvePlan } from '@/lib/chefs/print-plan'
import { buildRecipeDayGroups } from '@/lib/chefs/recipes-by-day'
import {
  buildServiceDays,
  dailyEmptyDayLabel,
  formatRangeForCopy,
  formatRangeLabel,
  isoDate,
  rangeServiceWeekKeys,
  rangeWindow,
} from '@/lib/chefs/service-days'
import { campusSupplyRows } from '@/lib/chefs/service-materials'
import { serviceStatusFromShipments, shipmentsForDay, shipmentsToCampuses } from '@/lib/chefs/shipment-to-campus'
import { countCampusesServings } from '@/lib/chefs/totals'
import type { Deliverable } from '@/lib/chefs/types'
import { NAV_ICONS, type NavIconSlug } from '@/lib/nav-icons'
import { resolveKitchenGate } from '@/pages/orders/kitchen-helpers'
import { groupIngredientRowsByType, type ShoppingListTab } from '@/pages/shopping-list-helpers'
import { KitchenOrdersSkeleton } from '@/skeletons/kitchen-orders-skeleton'

const FALLBACK_KITCHEN_NAME = 'My Kitchen'

const INGREDIENTS_ONLY_SECTIONS: PrintSections = { summary: false, ingredients: true, recipes: false }
const RECIPES_ONLY_SECTIONS: PrintSections = { summary: false, ingredients: false, recipes: true }
const DELIVERABLES_ONLY_SECTIONS: PrintSections = {
  summary: false,
  ingredients: false,
  recipes: false,
  deliverables: true,
}

/** Which of the three kitchen-list views a page renders. */
export type ChefKitchenView = ShoppingListTab

/**
 * Each view's sidebar nav slug — the single source of the page's title
 * (the `nav.<slug>` i18n key, same one the sidebar row renders) and its
 * icon (`NAV_ICONS`), so page header and nav row can never diverge.
 */
const VIEW_NAV_SLUG: Record<ChefKitchenView, NavIconSlug> = {
  ingredients: 'shopping-list',
  recipes: 'recipes',
  deliverables: 'deliverables',
}

/** On-screen row view — placed-order quantity + the optional forecast estimate. */
interface ShoppingRowView {
  key: string
  name: string
  unit: string
  /** Brand-selection slice — feeds the per-row IngredientBrandPicker. */
  brand: BrandPickerIngredient
  /** Placed-order demand (`totalRawQuantity`) — 0 on forecast-only rows. */
  actualQuantity: number
  /** Menu-derived estimate; null = no ordering history yet (unknown, not 0). */
  forecastQuantity: number | null
  /** A contributing menu item has no history → the estimate undercounts. */
  forecastMissingHistory: boolean
  source: ShoppingListRowSource
  /** FOOD / SUPPLY / UNCATEGORIZED — the "Supplies" fallback bucket. */
  category: ShoppingListCategory
  /** USDA CNDB category description; null lands in the "Other" bucket. */
  usdaCategory: string | null
}

/**
 * /shopping-list — the Ingredients page (the sidebar row reads
 * "Ingredients"; the path keeps its historical name so bookmarks and
 * the stable nav slug survive). Legacy pre-split `?tab=` deep links are
 * handled by the route table (`ShoppingListLegacyTabRoute` in
 * `routes/index.tsx`, beside the app's other legacy redirects).
 */
export function ShoppingListPage() {
  return <ChefKitchenPage view="ingredients" />
}

/**
 * Shared page shell for the three kitchen-list views. Exported for the
 * /recipes and /deliverables page modules.
 */
export function ChefKitchenPage({ view }: { view: ChefKitchenView }) {
  const kitchensQuery = useMyKitchensQuery()
  const activeKitchen = kitchensQuery.data?.me?.kitchenLocations?.[0] ?? null
  const kitchenLocationId = activeKitchen?.id ?? null
  const kitchenName = activeKitchen?.name ?? FALLBACK_KITCHEN_NAME
  // Resolved-to-zero-kitchens viewers get an explicit membership empty
  // state instead of the silent empty week (mirrors /orders/kitchen).
  const kitchensEmpty =
    resolveKitchenGate({
      pending: kitchensQuery.isPending,
      errored: kitchensQuery.isError,
      kitchenCount: kitchensQuery.data?.me?.kitchenLocations?.length ?? 0,
    }) === 'EMPTY'

  return (
    <ActiveKitchenProvider kitchenLocationId={kitchenLocationId}>
      <MeasurementPreferenceProvider kitchenOrganizationId={activeKitchen?.organizationId ?? null}>
        <SubstitutionResolutionBanner />
        <ChefKitchenBody
          view={view}
          kitchenName={kitchenName}
          kitchensPending={kitchensQuery.isPending}
          kitchensEmpty={kitchensEmpty}
        />
        <SubstitutionModal />
      </MeasurementPreferenceProvider>
    </ActiveKitchenProvider>
  )
}

function ChefKitchenBody({
  view,
  kitchenName,
  kitchensPending,
  kitchensEmpty,
}: {
  view: ChefKitchenView
  kitchenName: string
  kitchensPending: boolean
  kitchensEmpty: boolean
}) {
  const intl = useIntl()
  const navSlug = VIEW_NAV_SLUG[view]
  // `VIEW_NAV_SLUG` is typed against the whole `NavIconSlug` union, which is
  // broader than the three views this page renders, so the key is looked up
  // rather than cast. All three have catalog entries today.
  const title = intl.formatMessage({ id: `nav.${navSlug}` })
  const TitleIcon = NAV_ICONS[navSlug]
  const printLabel = `Print ${title}`
  // These are week-at-a-time jobs, so each surface opens Weekly. The range is
  // shared with /orders/kitchen and mirrored into `?date=&view=` so flipping
  // between the chef surfaces (or reloading) holds the chef's place — see
  // `useChefDateRange`.
  const { range, today, setRange } = useChefDateRange('weekly')
  const [printTarget, setPrintTarget] = useState<PrintTarget | null>(null)
  const [confirmingPreliminary, setConfirmingPreliminary] = useState(false)
  // The instant a printout's "are these counts final?" is judged (PROD-1452):
  // bumped when the last open order window on the range closes, so the
  // always-mounted packet loses its watermark without a reload.
  const [printClock, setPrintClock] = useState(() => new Date())

  // Recipe detail side panel (shared mechanics with the old /recipes browser).
  const recipeSlot = useRecipeDetailSlot()

  // Deliverables page's detail panel — same keep-mounted-through-close
  // mechanics as /orders/kitchen (200ms width collapse).
  const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null)
  const [fullscreenDeliverable, setFullscreenDeliverable] = useState<Deliverable | null>(null)
  const [renderedDeliverable, setRenderedDeliverable] = useState<Deliverable | null>(null)
  if (selectedDeliverable && selectedDeliverable !== renderedDeliverable) {
    setRenderedDeliverable(selectedDeliverable)
  }
  useEffect(() => {
    if (selectedDeliverable || !renderedDeliverable) {
      return
    }
    const t = window.setTimeout(() => setRenderedDeliverable(null), 200)
    return () => window.clearTimeout(t)
  }, [selectedDeliverable, renderedDeliverable])

  // Real data for the range: shipments drive the calendar + print
  // campus pages; the shopping-list aggregation drives the rows.
  const kitchenLocationId = useActiveKitchen()
  const { startDate, endDate } = useMemo(() => {
    const { start, end } = rangeWindow(range)
    return { startDate: isoDate(start), endDate: isoDate(end) }
  }, [range])
  const shipmentsQuery = useKitchenShipmentsQuery(
    { kitchenLocationId: kitchenLocationId ?? '', startDate, endDate },
    { enabled: Boolean(kitchenLocationId) },
  )
  const shipments = useMemo(() => shipmentsQuery.data?.shipments ?? [], [shipmentsQuery.data])
  const overdueTempLogDays = shipmentsQuery.data?.tempLogGate?.overdueDays ?? []
  const tempLogsBlocked = overdueTempLogDays.length > 0
  const campuses = useMemo(() => shipmentsToCampuses(shipments), [shipments])
  const supplyRows = useMemo(() => campusSupplyRows(campuses), [campuses])

  // Order-window model — mirrors /orders/kitchen: the week is "open"
  // (orders incoming) while ANY served school's deadline hasn't passed.
  // `servedSchoolDeadlines` rides the shipments response, so a future
  // week with zero shipments still resolves correctly.
  const orderCal = useMemo(
    () => buildChefOrderCalendar(shipments, today, shipmentsQuery.data?.servedSchoolDeadlines ?? []),
    [shipments, shipmentsQuery.data, today],
  )
  const serviceDays = useMemo(
    () => buildServiceDays(range, serviceStatusFromShipments(campuses, shipments)),
    [range, campuses, shipments],
  )

  // An order window is still open → an empty view means "no orders yet"
  // (schools can still order), not a genuine off day. Asked per DAY rather than
  // off the anchor's week: a custom window can straddle a week boundary, and
  // ordering is still incoming while ANY week it touches is open.
  const ordersIncoming = useMemo(
    () => rangeServiceWeekKeys(range).some((monday) => !orderCal.isWeekClosed(monday)),
    [orderCal, range],
  )

  // Deliverables uses the same resolved menu forecast while ordering is open.
  // Closed packing periods still need only the actual shipment data.
  const needsAggregation = view !== 'deliverables' || ordersIncoming

  // Forecast is requested ONLY while the window is open — a closed range can
  // never render forecast rows, so the forecast banner and the "ordering has
  // closed" empty state are mutually exclusive by construction. Waits for the
  // shipments read so `ordersIncoming` is settled before the first fetch.
  const aggregationQuery = useShoppingListQuery(
    { kitchenLocationId: kitchenLocationId ?? '', start: startDate, end: endDate, includeForecast: ordersIncoming },
    { enabled: Boolean(kitchenLocationId) && !shipmentsQuery.isPending && !tempLogsBlocked && needsAggregation },
  )

  const rows = useMemo(() => aggregationQuery.data?.rows ?? [], [aggregationQuery.data])
  // On-screen rows keep the forecast fields so estimates render visibly
  // distinct; the print packet only ever receives placed-order demand
  // (see `printIngredients`).
  const displayRows = useMemo<ShoppingRowView[]>(
    () =>
      rows.map((row) => ({
        // Per-SKU aggregation can emit two rows for one ingredient (two
        // brands bought separately) — the brand option keeps keys unique.
        key: `${row.ingredient.id}::${row.brandOptionId ?? ''}`,
        name: row.ingredient.name,
        unit: row.totalRawUnit,
        brand: shoppingListRowBrand(row),
        actualQuantity: row.totalRawQuantity,
        forecastQuantity: row.forecastRawQuantity,
        forecastMissingHistory: row.forecastMissingHistory,
        source: row.source,
        category: row.ingredient.category,
        usdaCategory: row.ingredient.usdaCategory,
      })),
    [rows],
  )
  // Grouped-by-USDA-type view of the same rows (Sheila 2026-08-22); null =
  // no row is USDA-mapped, so the list renders flat rather than as one
  // giant "Other" section.
  const ingredientGroups = useMemo(() => groupIngredientRowsByType(displayRows), [displayRows])
  // Print stays actuals-only: forecast-only rows are dropped and mixed
  // rows contribute just their placed-order quantity.
  const printIngredients = useMemo<MergedIngredient[]>(
    () =>
      printableShoppingListRows(rows).map((row) => ({
        key: `${row.ingredient.id}::${row.brandOptionId ?? ''}`,
        name: row.ingredient.name,
        unit: row.totalRawUnit,
        quantity: row.totalRawQuantity,
        brand: row.brandLabel,
        category: row.ingredient.category,
        usdaCategory: row.ingredient.usdaCategory,
      })),
    [rows],
  )
  // Recipes page: the day-grouped make-list. Pure fold over data the page
  // already fetches (shipments + the aggregation's forecast recipes).
  const recipeDayGroups = useMemo(
    () => buildRecipeDayGroups(serviceDays, shipments, aggregationQuery.data?.forecastRecipes ?? []),
    [serviceDays, shipments, aggregationQuery.data],
  )

  // Deliverables page: items totaled across every school, per day.
  const deliverablesDayGroups = useMemo(
    () =>
      buildDeliverablesDayGroups(
        serviceDays,
        shipments,
        ordersIncoming ? (aggregationQuery.data?.forecastMenuItems ?? []) : [],
      ),
    [serviceDays, shipments, ordersIncoming, aggregationQuery.data],
  )

  const forecastDayCount = aggregationQuery.data?.forecastDayCount ?? 0
  const showForecastBanner = needsAggregation && forecastDayCount > 0
  const noServiceInRange = serviceDays.length > 0 && serviceDays.every((d) => d.status === 'none')
  // A genuine off day (weekend) is the only case that should read "No service";
  // a served weekday that closed with zero orders reads "No orders". Mirrors
  // /orders/kitchen — weekday off-days aren't plumbed into this
  // shipment-derived view, so only weekends are treated as off days here.
  const selectedDayIsOffDay = useMemo(() => {
    if (range.granularity !== 'daily') {
      return false
    }
    const day = range.anchor.getDay()
    return day === 0 || day === 6
  }, [range])
  const dailyDayLabel = useMemo(() => dailyEmptyDayLabel(range, today), [range, today])

  /** One packet per page: ingredients (shopping list), recipes (batch-prep
   *  printout — same days + campus adaptation, so printed recipe quantities
   *  match the on-screen order-backed rows), deliverables (each day's items
   *  merged across every school for the packing bench). All actuals only:
   *  forecast entries never print (placed orders only).
   *
   *  Memoized because the packet stays AMBIENTLY MOUNTED as the page's print
   *  document (see the print-only portal below) — Chrome for Android fires
   *  `afterprint` immediately on menu prints (before the print framework
   *  snapshots the page), so any stage-on-beforeprint/clear-on-afterprint
   *  choreography tears the packet down before the snapshot. Same pattern as
   *  cafeteria/serve.tsx's ambient print target. */
  const buildTarget = useCallback(
    (kind: ChefKitchenView): PrintTarget | null => {
      // Range days with placed shipments, adapted for the print packet.
      // ACTUALS ONLY by construction — forecast days carry no shipments.
      const days: PrintDay[] = serviceDays
        .map((day) => {
          const iso = isoDate(day.date)
          return {
            iso,
            date: day.date,
            dateLabel: formatShortDay(day.date),
            campuses: shipmentsToCampuses(shipmentsForDay(shipments, iso)),
          }
        })
        .filter((d) => d.campuses.length > 0)
      if (days.length === 0) {
        return null
      }
      // PROD-1452 — a school on these days can still change its order: the
      // packet is PRELIMINARY, and the pages that school's counts reach carry
      // the INCOMPLETE ORDER watermark.
      const preliminary = printPreliminary({
        dayKeys: days.map((d) => d.iso),
        shipments,
        servedSchools: shipmentsQuery.data?.servedSchoolDeadlines ?? [],
        noServiceDays: shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
        now: printClock,
      })
      const campusIds = new Set<string>()
      const uniqueRecipeKeys = new Set<string>()
      for (const day of days) {
        for (const campus of day.campuses) {
          campusIds.add(campus.id)
          if (kind !== 'recipes') {
            continue
          }
          for (const block of campus.mealtimes) {
            for (const deliverable of block.deliverables) {
              for (const component of deliverable.components) {
                // Recipe-backed condiments count like any other made item
                // (mergeDayRecipes parity) — only recipe-less rows drop out.
                if (!component.recipe) {
                  continue
                }
                uniqueRecipeKeys.add(recipeKey(component.name, deliverable.unit))
              }
            }
          }
          // Milk sits on its own campus card (PROD-1502); its recipe still prints a card.
          for (const milk of campus.milk ?? []) {
            if (milk.component.recipe) {
              uniqueRecipeKeys.add(recipeKey(milk.component.name, 'x'))
            }
          }
        }
      }
      return {
        sections:
          kind === 'ingredients'
            ? INGREDIENTS_ONLY_SECTIONS
            : kind === 'recipes'
              ? RECIPES_ONLY_SECTIONS
              : DELIVERABLES_ONLY_SECTIONS,
        days,
        kitchenName,
        rangeDateLabel:
          days.length === 1
            ? formatShortDay(days[0]!.date)
            : formatShortDayRange(days[0]!.date, days[days.length - 1]!.date),
        ...(preliminary ? { preliminary } : {}),
        uniqueCampusCount: campusIds.size,
        uniqueRecipeCount: uniqueRecipeKeys.size,
        totalServings: days.reduce((sum, d) => sum + countCampusesServings(d.campuses), 0),
        // No per-day cover pages on these packets — a near-empty page per day
        // between content (Sheila 2026-08-04). The packet cover's TOC + each
        // page's date header carry the day context.
        showDayCovers: false,
        // Ingredients packet only: the real aggregation rows — the print's
        // Ingredient List section renders these instead of walking
        // (recipe-less) adapted campuses. ACTUALS ONLY: forecast estimates
        // never reach printed documents.
        ...(kind === 'ingredients' ? { ingredients: printIngredients } : {}),
      }
    },
    [serviceDays, shipments, kitchenName, printIngredients, shipmentsQuery.data, printClock],
  )

  // The page's always-mounted print document (current view, current range).
  const ambientPrintTarget = useMemo(() => buildTarget(view), [buildTarget, view])
  // Each time a school on the packet locks, re-judge it so the always-mounted
  // print copy drops that school's watermark (PROD-1452). The packet's days, not
  // the range's: a range day with no orders would re-judge for nothing.
  const ambientLocksAt = useMemo(
    () =>
      nextPrintLockAt({
        dayKeys: ambientPrintTarget?.days.map((day) => day.iso) ?? [],
        shipments,
        servedSchools: shipmentsQuery.data?.servedSchoolDeadlines ?? [],
        noServiceDays: shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
        now: printClock,
      }),
    [ambientPrintTarget, shipments, shipmentsQuery.data, printClock],
  )
  useOnPassed(ambientLocksAt, () => setPrintClock(new Date()))
  // A button-triggered print keeps its committed target through the print;
  // otherwise the ambient packet is the page's print document.
  const activePrintTarget = printTarget ?? ambientPrintTarget
  // Ingredients-only packet: the lone "Ingredient list" entry always starts
  // on page 2 (the cover is a single page), so an empty-height resolve gives
  // correct TOC numbers without a measurement pass.
  const activePrintToc = useMemo(
    () => (activePrintTarget ? resolvePlan(buildPrintStructure(activePrintTarget), new Map()).toc : null),
    [activePrintTarget],
  )

  // Why the Print button is (or is not) actionable. An empty range used to
  // leave the button enabled and no-op on click — invisible to the user, and
  // indistinguishable from a broken PDF export on a phone.
  const availability = printAvailability({
    hasPrintTarget: ambientPrintTarget !== null,
    serviceDayCount: serviceDays.length,
  })
  const printBlockedReason = availability.canPrint ? null : availability.reason

  function startPrint() {
    if (!ambientPrintTarget) {
      return
    }
    setPrintTarget(ambientPrintTarget)
    window.setTimeout(() => window.print(), 200)
  }

  // A preliminary packet asks first (PROD-1452); printing itself is never blocked.
  function triggerPrint() {
    if (ambientPrintTarget?.preliminary) {
      setConfirmingPreliminary(true)
      return
    }
    startPrint()
  }

  // Cmd/Ctrl+P runs the same flow as the Print button. Menu / mobile share
  // sheet prints need NO event choreography: the ambient packet stays
  // mounted, so the system print's snapshot picks it up with zero JS —
  // stage-on-beforeprint was torn down early by Android Chrome's
  // immediately-fired `afterprint` and printed a blank page.
  usePrintAction({
    onPrintRequest: triggerPrint,
    printing: printTarget !== null,
  })

  useEffect(() => {
    if (!printTarget) {
      return
    }
    // The browser names the PDF after document.title — say what the packet
    // is ("Ordo_Ingredients_<range>" / "Ordo_Recipes_<range>"). The clear is
    // DEFERRED past `afterprint`: Chrome for Android fires that event before
    // its print framework reads the page, so an immediate clear prints blank
    // (the ambient packet below softens it here, but the committed target
    // must outlive the read too).
    const previousTitle = document.title
    document.title = printDocumentTitle(printTarget)
    const stopTeardown = listenAfterPrintTeardown(() => setPrintTarget(null))
    return () => {
      stopTeardown()
      document.title = previousTitle
    }
  }, [printTarget])

  const isNarrow = useIsNarrow()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isScrolled = useIsScrolled(scrollRef)
  const openSubstitutionLedger = useSubstitutionModal((s) => s.openModal)

  // `schoolCount` is the union of shipment schools + forecast-contributing
  // schools when the forecast is on, so a forecast-only week renders rows
  // instead of the empty state.
  const campusCount = aggregationQuery.data?.schoolCount ?? campuses.length
  const summary = `${displayRows.length} unique ingredient${displayRows.length === 1 ? '' : 's'} across ${campusCount} ${
    campusCount === 1 ? 'school' : 'schools'
  }`

  if (
    kitchensPending ||
    (Boolean(kitchenLocationId) &&
      (shipmentsQuery.isPending || (!tempLogsBlocked && needsAggregation && aggregationQuery.isPending)))
  ) {
    return <KitchenOrdersSkeleton />
  }

  // Zero kitchens resolved (settled, no error): say WHY the list is empty
  // rather than rendering a blank week for "My Kitchen".
  if (kitchensEmpty) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={TitleIcon} title={title} className="mb-0" />
        </div>
        <div className="px-4 pt-6 lg:px-0">
          <EmptyState
            icon={<ChefHat />}
            title="No kitchen memberships"
            description="Your kitchen's lists show up here once you're added as an admin or staff to a kitchen organization."
          />
        </div>
      </div>
    )
  }

  // Withheld shipments are not an empty order list. Match Orders' unlock path.
  if (tempLogsBlocked) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={TitleIcon} title={title} className="mb-0" />
        </div>
        <TempLogGate days={overdueTempLogDays} />
      </div>
    )
  }

  if (needsAggregation && aggregationQuery.error) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={TitleIcon} title={title} className="mb-0" />
        </div>
        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-destructive">Couldn&apos;t load the {title.toLowerCase()}.</p>
            <p className="mt-1 text-xs text-muted-foreground">{aggregationQuery.error.message}</p>
          </div>
          <ChipButton className={CHIP_CLASS} onClick={() => void aggregationQuery.refetch()}>
            Retry
          </ChipButton>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={TitleIcon} title={title} className="mb-0" />
        </div>
        {showForecastBanner ? (
          <div className="pt-4 lg:pt-5">
            <Alert variant="warning" className="mx-4 flex items-start gap-3 lg:mx-0">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
              <div>
                <div className="font-semibold">Forecast — not actual orders</div>
                <div className="opacity-90">
                  Ordering is still open for {formatRangeForCopy(range, today)}. Menus without placed orders show
                  estimates from this kitchen&apos;s past orders — final numbers arrive after the order cutoff.
                </div>
              </div>
            </Alert>
          </div>
        ) : null}

        <div className="relative z-10 bg-background">
          <div className="flex items-center justify-between gap-3 px-2 pb-3 pt-4 lg:py-5 lg:pl-0 lg:pr-3">
            <DateRangeStepper range={range} today={today} onRangeChange={setRange} />
            <div className="flex items-center gap-2">
              {view === 'ingredients' ? (
                <ChipButton
                  Icon={ArrowLeftRight}
                  className={cn(CHIP_CLASS, 'hidden lg:inline-flex')}
                  onClick={() => openSubstitutionLedger({ view: 'form' })}>
                  Substitute an Ingredient
                </ChipButton>
              ) : null}
              <ChipButton
                variant="default"
                Icon={Printer}
                className={cn(CHIP_CLASS_PRIMARY, 'hidden lg:inline-flex')}
                disabled={printBlockedReason !== null}
                title={printBlockedReason ?? undefined}
                onClick={triggerPrint}>
                {printBlockedReason ?? printLabel}
              </ChipButton>
            </div>
          </div>
        </div>

        {view === 'ingredients' ? (
          <div className="flex min-h-0 flex-1 flex-col lg:pr-1">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
              {campusCount > 0 ? (
                <div className="relative z-10 px-2 lg:px-0">
                  <div
                    className={cn(
                      'rounded-t-2xl border border-b-0 border-border bg-background px-4 py-4 transition-[box-shadow] duration-200 ease-out lg:px-6 lg:py-5',
                      isScrolled && 'shadow-[0_6px_8px_-6px_rgba(0,0,0,0.12)]',
                    )}>
                    <h2 className="text-base font-medium text-foreground">Ingredients</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">{summary}</p>
                  </div>
                </div>
              ) : null}
              <div
                ref={scrollRef}
                data-scroll-container
                className="min-h-0 flex-1 overflow-y-auto pb-24 lg:pb-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {campusCount === 0 ? (
                  // Mirrors /orders/kitchen: an open window reads "orders are
                  // still incoming"; only a genuinely closed window falls to the
                  // "ordering has closed" copy (or "no service" on weekends).
                  ordersIncoming ? (
                    <NoServiceEmptyState ordersIncoming weekday={dailyDayLabel} />
                  ) : noServiceInRange ? (
                    <NoServiceEmptyState weekday={dailyDayLabel} isOffDay={selectedDayIsOffDay} />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
                      <p className="text-lg font-medium">No ingredients for this period.</p>
                      <p className="text-sm text-muted-foreground">Try selecting a different date range.</p>
                    </div>
                  )
                ) : (
                  <div className="px-2 pb-4 lg:px-0">
                    <div className="overflow-hidden rounded-b-2xl border border-t-0 border-border bg-background shadow-xs">
                      <div className="flex flex-col">
                        {(ingredientGroups ?? [{ label: null, rows: displayRows }]).map((group) => (
                          <div key={group.label ?? '__flat'}>
                            {group.label !== null ? (
                              <ListGroupHeader label={group.label} count={group.rows.length} />
                            ) : null}
                            <div className="flex flex-col divide-y divide-border">
                              {group.rows.map((row) => (
                                <IngredientListRow key={row.key} row={row} />
                              ))}
                            </div>
                          </div>
                        ))}
                        {displayRows.length === 0 ? (
                          <p className="px-4 py-6 text-center text-sm text-muted-foreground lg:px-6">
                            No ingredients on file.
                          </p>
                        ) : null}
                        <SuppliesTable embedded includePackaging rows={supplyRows} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {view === 'recipes' ? (
          <div className="flex min-h-0 flex-1 items-stretch gap-2">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:pr-1">
              <div
                data-scroll-container
                className="min-h-0 flex-1 overflow-y-auto pb-24 pt-1 lg:pb-4 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                <ShoppingRecipesTab
                  dayGroups={recipeDayGroups}
                  ordersIncoming={ordersIncoming}
                  activeComponentId={recipeSlot.activeComponentId}
                  onSelect={(id, variantId) => recipeSlot.setActiveComponentId(id, variantId)}
                  emptyFallback={
                    ordersIncoming ? (
                      <NoServiceEmptyState ordersIncoming weekday={dailyDayLabel} />
                    ) : noServiceInRange ? (
                      <NoServiceEmptyState weekday={dailyDayLabel} isOffDay={selectedDayIsOffDay} />
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
                        <p className="text-lg font-medium">No recipes for this period.</p>
                        <p className="text-sm text-muted-foreground">Try selecting a different date range.</p>
                      </div>
                    )
                  }
                />
              </div>
            </div>
            <RecipeDetailSlot slot={recipeSlot} isNarrow={isNarrow} paddingClassName="pb-4 pt-1" />
          </div>
        ) : null}

        {view === 'deliverables' ? (
          <div className="flex min-h-0 flex-1 items-stretch gap-2">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:pr-1">
              <div
                data-scroll-container
                className="min-h-0 flex-1 overflow-y-auto pb-24 pt-1 lg:pb-4 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                <ShoppingDeliverablesTab
                  dayGroups={deliverablesDayGroups}
                  supplyRows={supplyRows}
                  ordersIncoming={ordersIncoming}
                  onSelectDeliverable={setSelectedDeliverable}
                  selectedDeliverableId={selectedDeliverable?.id}
                  emptyFallback={
                    ordersIncoming ? (
                      <NoServiceEmptyState ordersIncoming weekday={dailyDayLabel} />
                    ) : noServiceInRange ? (
                      <NoServiceEmptyState weekday={dailyDayLabel} isOffDay={selectedDayIsOffDay} />
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
                        <p className="text-lg font-medium">Nothing to pack for this period.</p>
                        <p className="text-sm text-muted-foreground">Try selecting a different date range.</p>
                      </div>
                    )
                  }
                />
              </div>
            </div>
            {/* Deliverable side peek — same column mechanics as /orders/kitchen. */}
            {!isNarrow ? (
              <div
                className={cn(
                  'relative shrink-0 overflow-hidden transition-[width] duration-200 ease-out',
                  selectedDeliverable ? 'w-[clamp(440px,45vw,600px)]' : 'w-0',
                )}>
                {renderedDeliverable ? (
                  <div className="absolute inset-y-0 left-0 w-[clamp(440px,45vw,600px)] pb-4 pt-1">
                    <DeliverableDetailPanel
                      deliverable={renderedDeliverable}
                      onClose={() => setSelectedDeliverable(null)}
                      onFullscreen={(d) => {
                        setFullscreenDeliverable(d)
                        setSelectedDeliverable(null)
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <RecipeDetailNarrowOverlay slot={recipeSlot} isNarrow={isNarrow} />
      <RecipeDetailFullscreenDialog slot={recipeSlot} />

      {isNarrow && selectedDeliverable ? (
        <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-background">
          <DeliverableDetailPanel deliverable={selectedDeliverable} onClose={() => setSelectedDeliverable(null)} />
        </div>
      ) : null}

      {/* Desktop "Full screen" → centered Dialog (mirrors /orders/kitchen). */}
      <Dialog open={!!fullscreenDeliverable} onOpenChange={(next) => (next ? null : setFullscreenDeliverable(null))}>
        <DialogContent hideCloseButton className="p-0 sm:max-w-3xl sm:rounded-2xl sm:h-[85vh]">
          {/* Radix requires an accessible name; the panel renders its own
              heading, so this is the screen-reader-only equivalent. */}
          <DialogTitle className="sr-only">{fullscreenDeliverable?.name ?? 'Deliverable'}</DialogTitle>
          {fullscreenDeliverable ? (
            <DeliverableDetailPanel
              variant="fullscreen"
              deliverable={fullscreenDeliverable}
              onClose={() => setFullscreenDeliverable(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {isNarrow ? (
        view === 'ingredients' ? (
          <MobileActionPill
            primaryLabel={printLabel}
            printBlockedReason={printBlockedReason}
            SecondaryIcon={ArrowLeftRight}
            secondaryLabel="Substitute"
            onSecondary={() => openSubstitutionLedger({ view: 'form' })}
            onPrintAll={triggerPrint}
          />
        ) : (
          <MobileActionPill
            primaryLabel={printLabel}
            printBlockedReason={printBlockedReason}
            onPrintAll={triggerPrint}
          />
        )
      ) : null}

      {/* AMBIENT print document — ALWAYS mounted, not just during a
          button-triggered print, so a system print (browser menu / mobile
          share sheet) snapshots it with no JS involved. Chrome for Android
          fires `afterprint` immediately (before its print framework
          snapshots), so mount-on-beforeprint / clear-on-afterprint prints a
          blank page there. Same pattern as cafeteria/serve.tsx's ambient
          print target.

          It is mounted unconditionally because the app shell is
          `print-screen-only`: with no portal there is nothing left to print
          and the page comes out BLANK. An empty range renders an explanation
          sheet rather than nothing. */}
      {createPortal(
        <div className="print-only">
          {activePrintTarget && activePrintToc ? (
            <PrintRoot target={activePrintTarget} toc={activePrintToc} />
          ) : (
            // NEVER render nothing here. The app shell is `print-screen-only`,
            // so an absent portal means a system print (Chrome's menu / the
            // Android share sheet) snapshots a genuinely BLANK page — the exact
            // symptom #2088/#2103 fixed, from a different cause.
            <PrintEmptySheet
              kitchenName={kitchenName}
              rangeDateLabel={formatRangeLabel(range, today)}
              title={title}
              reason={printBlockedReason ?? 'Nothing to print for this range'}
            />
          )}
        </div>,
        document.body,
      )}
      <PreliminaryPrintConfirm
        open={confirmingPreliminary}
        onOpenChange={setConfirmingPreliminary}
        locksAtLabel={ambientPrintTarget?.preliminary?.locksAtLabel ?? null}
        onConfirm={startPrint}
      />
    </>
  )
}

/** One ingredient line — quantity column, name + brand picker, forecast badge. */
function IngredientListRow({ row }: { row: ShoppingRowView }) {
  return (
    <div className="flex items-baseline gap-3 px-4 py-2.5 text-sm lg:px-6">
      <span className="flex w-24 shrink-0 flex-col">
        {row.source !== 'forecast' ? (
          <span className="font-medium text-foreground">
            {formatIngredientQuantity(row.actualQuantity)} {row.unit}
          </span>
        ) : null}
        {row.source !== 'orders' ? (
          row.forecastQuantity !== null ? (
            <span
              className="text-xs italic text-muted-foreground"
              title={
                row.forecastMissingHistory
                  ? 'Partial estimate — some menu items have no ordering history yet, so the real need may be higher.'
                  : undefined
              }>
              {row.source === 'mixed' ? '+ ' : ''}~{formatIngredientQuantity(row.forecastQuantity)} {row.unit}
            </span>
          ) : (
            <span className="text-xs italic text-muted-foreground">No history yet</span>
          )
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
        <IngredientSubstitutionDisplay ingredientName={row.name} ingredientUnit={row.unit} />
        {/* Brand dropdown: preselected to the resolved brand (pin → kitchen
            choice → variety → default); a pick persists as this kitchen's
            standing selection. Purchasing-only — never touches family
            surfaces. */}
        <IngredientBrandPicker ingredient={row.brand} />
      </span>
      {row.source !== 'orders' ? (
        <Badge variant="warning" className="shrink-0">
          Forecast
        </Badge>
      ) : null}
    </div>
  )
}
